import Spacings from '~/modules/ui-kit/spacings';
import messages from './messages';
import moment from 'moment';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import formMessages from '~/components/form-messages';
import { SimpleExperienceResource } from '~/generated/types.gen';
import { useAppForm } from '~/components/Form';

const schema = (intl: IntlShape) =>
  z.object({
    location: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    description: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    experience: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    turnId: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
    title: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    eventDate: z
      .string({
        error: intl.formatMessage(formMessages.required),
      })
      .min(1, intl.formatMessage(formMessages.required)),
    eventTime: z.string({
      error: intl.formatMessage(formMessages.required),
    }),
  });

type FormValues = {
  eventDate: string;
  location: string;
  description: string;
  title: string;
  experience: string;
  turnId: string;
  eventTime: string;
  selectableTurns?: Application[];
};

type Application = {
  turn: {
    id: string;
    route?: {
      name: string;
    };
  };
  shift: {
    startDate: string;
  };
};

type CreateExperienceFormProps = {
  initialValues?: FormValues;
  publisherId: number;
  onSubmit: (values: SimpleExperienceResource) => void;
};

const CreateExperienceForm = (props: CreateExperienceFormProps) => {
  const intl = useIntl();

  const form = useAppForm({
    defaultValues: props.initialValues || {
      location: '',
      title: '',
      experience: 'positive',
      description: '',
      eventDate: '',
      turnId: '',
      eventTime: '',
    },
    validators: {
      onChange: schema(intl),
    },
    onSubmit: ({ value }) => {
      props.onSubmit({
        isPositive: value.experience === 'positive',
        title: value.title,
        publisherId: props.publisherId,
        eventDate: `${moment(value.eventDate).format('YYYY-MM-DD')}T${moment(value.eventTime, 'HH:mm').format('HH:mm:ss')}`,
        location: value.location,
        description: value.description,
      });
    },
  });

  const createTurnSelectOptions = () =>
    (props.initialValues?.selectableTurns ?? []).map(
      (application: Application) => {
        const {
          turn: { id, route },
          shift,
        } = application;
        return {
          value: id,
          label: `${moment(shift.startDate).format('LLL')} - ${route?.name}`,
        };
      }
    );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Inset>
        <Spacings.Stack scale="l">
          <form.AppField name="experience">
            {(field) => (
              <field.RadioGroupField
                label={<FormattedMessage {...messages.positiveLabel} />}
                row
                options={[
                  {
                    value: 'positive',
                    label: intl.formatMessage(messages.positive),
                  },
                  {
                    value: 'negative',
                    label: intl.formatMessage(messages.negative),
                  },
                ]}
              />
            )}
          </form.AppField>

          <form.AppField name="turnId">
            {(field) => (
              <field.SelectField
                label={<FormattedMessage {...messages.selectTurnLabel} />}
                options={createTurnSelectOptions()}
              />
            )}
          </form.AppField>

          <Spacings.Inline>
            <form.AppField name="eventDate">
              {(field) => (
                <field.DateField
                  label={<FormattedMessage {...messages.eventDateLabel} />}
                  format="DD.MM.YYYY"
                  maxDate={moment()}
                />
              )}
            </form.AppField>

            <form.AppField name="eventTime">
              {(field) => (
                <field.TimeField
                  label={<FormattedMessage {...messages.eventTimeLabel} />}
                  format="HH:mm"
                  disableFuture
                />
              )}
            </form.AppField>
          </Spacings.Inline>

          <form.AppField name="location">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.locationLabel} />}
              />
            )}
          </form.AppField>

          <form.AppField name="title">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.titleLabel} />}
                placeholder={intl.formatMessage(messages.titleLabel)}
                multiline
                rows={3}
              />
            )}
          </form.AppField>

          <form.AppField name="description">
            {(field) => (
              <field.TextField
                label={<FormattedMessage {...messages.descriptionLabel} />}
                placeholder={intl.formatMessage(messages.descriptionLabel)}
                multiline
                rows={3}
              />
            )}
          </form.AppField>

          <form.AppForm>
            <form.SubmitButton>
              <FormattedMessage {...messages.saveButton} />
            </form.SubmitButton>
          </form.AppForm>
        </Spacings.Stack>
      </Spacings.Inset>
    </form>
  );
};
export default CreateExperienceForm;
